import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Form } from "react-bootstrap";
import { Pie, Line } from "react-chartjs-2";
import Axis from "../../assets/images/banks/axis-bank.svg";
import HDFC from "../../assets/images/banks/hdfc-bank.svg";
import ICICI from "../../assets/images/banks/icici-bank.svg";
import KM from "../../assets/images/banks/kotak-mahindra-bank.svg";
import RBL from "../../assets/images/banks/rbl-bank.svg";
import SBI from "../../assets/images/banks/sbi.png";
import "./calc.css";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const EmiCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [interest, setInterest] = useState(6);
  const [tenure, setTenure] = useState(5);

  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interest, tenure]);

  const calculateEMI = () => {
    const monthlyRate = interest / 12 / 100;
    const months = tenure * 12;

    const emiValue =
      (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);

    let balance = loanAmount;
    let totalInterestCalc = 0;
    const rows = [];

    for (let i = 1; i <= months; i++) {
      const interestPart = balance * monthlyRate;
      const principalPart = emiValue - interestPart;
      balance -= principalPart;
      totalInterestCalc += interestPart;

      rows.push({
        month: i,
        principal: principalPart,
        interest: interestPart,
        emi: emiValue,
        balance: balance > 0 ? balance : 0
      });
    }

    setEmi(emiValue);
    setTotalInterest(totalInterestCalc);
    setTotalAmount(loanAmount + totalInterestCalc);
    setSchedule(rows);
  };

  /* ---------------- Charts ---------------- */

  const pieData = {
    labels: ["Principal", "Interest"],
    datasets: [
      {
        data: [loanAmount, totalInterest],
        backgroundColor: ["#CB3038", "#f5b041"]
      }
    ]
  };

  const lineData = {
    labels: schedule.map((row) => `M${row.month}`),
    datasets: [
      {
        label: "Outstanding Balance",
        data: schedule.map((row) => row.balance),
        borderColor: "#CB3038",
        fill: false,
        tension: 0.3
      }
    ]
  };

  const banks = [
    { name: "SBI", logo: SBI },
    { name: "Axis Bank", logo: Axis },
    { name: "HDFC Bank", logo: HDFC },
    { name: "ICICI Bank", logo: ICICI },
    { name: "Kotak Bank", logo: KM },
    { name: "RBL Bank", logo: RBL }
  ];

  return (
    <Container className="my-5">
      {/* Header */}
      <Row className="text-center mb-5">
        <Col>
          <p className="fw-bold">EMI Calculator</p>
          <h3 className="red-text">
            Plan your loan better with a detailed EMI breakup
          </h3>
        </Col>
      </Row>

      {/* Calculator */}
      <Row className="mb-5">
        <Col md={8}>
          <Card className="shadow-sm p-4 mb-4">
            {/* Loan Amount */}
            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between">
                <h5 className="red-text">Loan Amount</h5>
                <Form.Control
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  style={{ width: "150px" }}
                />
              </div>
              <Form.Range
                min={100000}
                max={10000000}
                step={50000}
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
              />
            </Form.Group>

            {/* Interest */}
            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between">
                <h5 className="red-text">Rate of Interest (%) (Per Annum)</h5>
                <Form.Control
                  type="number"
                  value={interest}
                  onChange={(e) => setInterest(Number(e.target.value))}
                  style={{ width: "100px" }}
                />
              </div>
              <Form.Range
                min={1}
                max={30}
                step={0.1}
                value={interest}
                onChange={(e) => setInterest(Number(e.target.value))}
              />
            </Form.Group>

            {/* Tenure */}
            <Form.Group>
              <div className="d-flex justify-content-between">
                <h5 className="red-text">Loan Tenure (Years)</h5>
                <Form.Control
                  type="number"
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                  style={{ width: "100px" }}
                />
              </div>
              <Form.Range
                min={1}
                max={30}
                step={1}
                value={tenure}
                onChange={(e) => setTenure(Number(e.target.value))}
              />
            </Form.Group>
          </Card>
        </Col>

        {/* Pie Chart */}
        <Col md={4}>
          <Card className="shadow-sm p-3">
            <Pie data={pieData} />
          </Card>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-5 text-center">
        {[
          { label: "Monthly EMI", value: emi },
          { label: "Principal Amount", value: loanAmount },
          { label: "Total Interest", value: totalInterest },
          { label: "Total Amount Payable", value: totalAmount }
        ].map((item, i) => (
          <Col md={3} sm={6} key={i} className="mb-3">
            <Card className="shadow-sm p-3">
              <h6 className="text-muted">{item.label}</h6>
              <h5 className="fw-bold red-text">₹ {item.value.toFixed(0)}</h5>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Line Graph */}
      <Row className="mb-5">
        <Col>
          <Card className="shadow-sm p-4">
            <h5 className="mb-3 red-text">Loan Balance Over Time</h5>
            <Line data={lineData} />
          </Card>
        </Col>
      </Row>
      {/* Our Bank Tie ups */}
      <Row>
        <section className="bank-section py-5">
          <div className="container text-center">
            <h3 className="mb-4 fw-bold">Our Bank Tie-Ups</h3>

            <div className="bank-slider">
              <div className="bank-track">
                {[...banks, ...banks].map((bank, index) => (
                  <div className="bank-card" key={index}>
                    <img
                      src={bank.logo}
                      alt={bank.name}
                      className="img-fluid"
                    />
                    <p className="mt-2">{bank.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </Row>

      {/* Amortization Table */}
      <Row>
        <Col>
          <Card className="shadow-sm p-4">
            <h5 className="mb-3 red-text">Detailed Amortization Schedule</h5>
            <Table responsive bordered hover>
              <thead>
                <tr>
                  <th className="text-muted">Month</th>
                  <th className="text-muted">Principal</th>
                  <th className="text-muted">Interest</th>
                  <th className="text-muted">EMI</th>
                  <th className="text-muted">Balance</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, index) => (
                  <tr key={index}>
                    <td>{row.month}</td>
                    <td>₹ {row.principal.toFixed(0)}</td>
                    <td>₹ {row.interest.toFixed(0)}</td>
                    <td>₹ {row.emi.toFixed(0)}</td>
                    <td>₹ {row.balance.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EmiCalculator;
